import { useModal } from "hooks/useModal"

import { memo, useCallback, useState } from "react"
import styled from "styled-components"
import { AppState, useAppDispatch, useAppSelector } from "reducers/store"
import Heading from "components/Typography/Heading"
import { clearAccount } from "reducers/accounts/accounts-actions"
import ModalV2 from "components/EvtKit/modal/ModalV2"
import Tabs from "components/EvtKit/tabs/Tabs"
import TabsListGradient from "components/EvtKit/tabs/tabsGradient/TabsListGradient"
import TabsTriggerGradient from "components/EvtKit/tabs/tabsGradient/TabsTriggerGradient"
import TabsContent from "components/EvtKit/tabs/TabsContent"
import BlockOrActiveEnterpriseAccountComponent from "./BlockOrActiveEnterpriseAccountComponent"
import { useMutation, useQueryClient } from "react-query"
import ApiAccounts from "api/ApiAccounts"
import { ACCOUNTS_FETCH } from "reducers/accounts/actions-types"
import useHandleApiError from "hooks/useHandleApiErrors"
import { showToast } from "reducers/toast/toast-actions"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 28px 0px 24px;
  gap: 32px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const TabsListContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  height: 40px;
`

const tabIds = {
  block: "block",
  active: "active"
}

const BlockOrActiveEnterpriseAccountModal = () => {
  const appDispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const { account } = useAppSelector((x: AppState) => x.accounts)

  const { isOpen, type, closeModal } = useModal()

  const [inputValues, setInputValues] = useState({
    blockReason: "",
    activeReason: ""
  })

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    setInputValues((prevValues) => ({
      ...prevValues,
      [name]: value
    }))
  }, [])

  const handleCleanModalState = useCallback(() => {
    queryClient.invalidateQueries(ACCOUNTS_FETCH)
    appDispatch(clearAccount())
    closeModal()
    setInputValues({
      blockReason: "",
      activeReason: ""
    })
  }, [closeModal])

  const disabledBlockButton = account?.status === "blocked"
  const disabledActiveButton = account?.status === "active"

  const blockEnterpriseAccountMutation = useMutation({
    mutationFn: async (data: App.AccountBlockEnterprisePayload) => {
      return new ApiAccounts().pathBlockAccountEnterpriseAndPayByInvoice(account.id, data)
    },
    onSuccess: () => {
      handleCleanModalState()
      queryClient.invalidateQueries(ACCOUNTS_FETCH)
      appDispatch(showToast("Conta bloqueada com sucesso!", "success"))
    },
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Não foi possível bloquear essa conta, tente novamente mais tarde.")
    }
  })

  const handleClickBlockEnterpriseAccount = useCallback(() => {
    blockEnterpriseAccountMutation.mutate({
      prodPrefixUrl: account.product,
      blockReason: inputValues.blockReason
    })
  }, [account, inputValues.blockReason])

  const activeEnterpriseAccountMutation = useMutation({
    mutationFn: async (data: App.AccountActiveEnterprisePayload) => {
      return new ApiAccounts().pathActiveAccountEnterpriseAndPayByInvoice(account.id, data)
    },
    onSuccess: () => {
      handleCleanModalState()
      queryClient.invalidateQueries(ACCOUNTS_FETCH)
      appDispatch(showToast("Conta reativada com sucesso!", "success"))
    },
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Não foi possível reativar essa conta, tente novamente mais tarde.")
    }
  })

  const handleClickActiveEnterpriseAccount = useCallback(() => {
    activeEnterpriseAccountMutation.mutate({
      prodPrefixUrl: account.product,
      activeReason: inputValues.activeReason
    })
  }, [account, inputValues.activeReason])

  const tooltipMessage = account?.status === "active" ? "Essa conta está ativa." : "Essa conta está bloqueada."

  return (
    <ModalV2
      openModal={isOpen && type === "blockEnterpriseAccount"}
      closeModal={handleCleanModalState}
      size="1-lg"
      height="md"
    >
      <Container>
        <Heading variant="h4" color="primary-dark">
          Bloqueio ou reativação de conta enterprise
        </Heading>
        <Content>
          <Tabs defaultActiveTabId={tabIds.block}>
            <TabsListContainer>
              <TabsListGradient className="fill-width">
                <TabsTriggerGradient bgClassName="md" label="Bloquear conta" triggerId={tabIds.block} />
                <TabsTriggerGradient bgClassName="md" label="Reativar conta" triggerId={tabIds.active} />
              </TabsListGradient>
            </TabsListContainer>
            <TabsContent triggerId={tabIds.block}>
              <BlockOrActiveEnterpriseAccountComponent
                title="Esta função permite bloquear uma conta Enterprise que paga por invoice"
                buttonLabel="Bloquear conta"
                inputLabel="Motivo de bloqueio"
                inputName="blockReason"
                inputValue={inputValues.blockReason}
                disabledButton={disabledBlockButton || blockEnterpriseAccountMutation.isLoading}
                onClickCancel={handleCleanModalState}
                onClick={handleClickBlockEnterpriseAccount}
                handleInputChange={handleInputChange}
                tooltipMessage={tooltipMessage}
                disabledInput={account?.status === "blocked"}
                loading={blockEnterpriseAccountMutation.isLoading}
              />
            </TabsContent>
            <TabsContent triggerId={tabIds.active}>
              <BlockOrActiveEnterpriseAccountComponent
                title="Esta função permite reativar uma conta Enterprise que paga por invoice"
                buttonLabel="Reativar conta"
                inputLabel="Motivo de reativação"
                inputName="activeReason"
                inputValue={inputValues.activeReason}
                disabledButton={disabledActiveButton || activeEnterpriseAccountMutation.isLoading}
                onClickCancel={handleCleanModalState}
                onClick={handleClickActiveEnterpriseAccount}
                handleInputChange={handleInputChange}
                tooltipMessage={tooltipMessage}
                disabledInput={account?.status === "active"}
                loading={activeEnterpriseAccountMutation.isLoading}
              />
            </TabsContent>
          </Tabs>
        </Content>
      </Container>
    </ModalV2>
  )
}

export default memo(BlockOrActiveEnterpriseAccountModal)
